// Pure, locale-aware duration formatter (contract 04 §14). PORT-AND-SHARPEN of the legacy
// module-private `formatSecondsAsDuration` (`format.js:648–660`). Contract §14 keeps that hand-rolled
// `Nh Nm Ns` builder as the DETERMINISTIC FALLBACK for engines without `Intl.DurationFormat`, and
// prefers `Intl.DurationFormat` (locale-aware) when it is available. Sharpened per the ticket:
//   • the fallback is now EXPORTED (`formatDurationFallback`) — it was module-private in v3;
//   • `Intl.DurationFormat` is FEATURE-DETECTED at runtime (it is absent on older engines and is not
//     yet in the pinned TypeScript lib — see `intl-augment.d.ts`, which does not assert availability).
//
// The fallback preserves the legacy 0-pad rule verbatim: seconds are zero-padded to two digits only
// when `s < 10 && (m > 0 || h > 0)` (`format.js:658`). Zero Vue, zero Pinia, zero network.

import { FALLBACK_LOCALE, SENTINEL, safeLocale } from './createFormatter'
import type { FormatDurationOptions } from './types'

const SECONDS_PER_HOUR = 3600
const SECONDS_PER_MINUTE = 60

interface DurationParts {
  hours: number
  minutes: number
  seconds: number
}

function splitSeconds(totalSeconds: number): DurationParts {
  return {
    hours: Math.floor(totalSeconds / SECONDS_PER_HOUR),
    minutes: Math.floor((totalSeconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE),
    seconds: Math.round(totalSeconds % SECONDS_PER_MINUTE),
  }
}

/**
 * Deterministic `Nh Nm Ns` fallback (legacy `formatSecondsAsDuration`). Engine-independent — no Intl.
 * Higher units are shown only when non-zero; seconds are always shown, zero-padded to two digits only
 * when below 10 AND a higher unit is present (the legacy rule). `formatDurationFallback(3723)` →
 * `"1h 2m 03s"`; `(5)` → `"5s"`; `(65)` → `"1m 05s"`.
 */
export function formatDurationFallback(totalSeconds: number): string {
  const { hours, minutes, seconds } = splitSeconds(totalSeconds)

  const parts: string[] = []
  if (hours > 0) parts.push(`${hours}h`)
  if (minutes > 0 || hours > 0) parts.push(`${minutes}m`)
  const pad = seconds < 10 && (minutes > 0 || hours > 0) ? '0' : ''
  parts.push(`${pad}${seconds}s`)

  return parts.join(' ')
}

/**
 * Deterministic wall-clock rendering: `m:ss` (uncapped minutes, two-digit seconds), `h:mm:ss`
 * once hours are present. Locale-independent by design — a countdown reads the same everywhere.
 * Negative input clamps to `0:00`: a wall clock has no meaningful negative rendering.
 */
function formatClock(value: number): string {
  const total = Math.max(0, Math.trunc(value))
  const hours = Math.floor(total / SECONDS_PER_HOUR)
  const minutes = Math.floor((total % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE)
  const seconds = String(total % SECONDS_PER_MINUTE).padStart(2, '0')
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, '0')}:${seconds}`
    : `${minutes}:${seconds}`
}

const cache = new Map<string, Intl.DurationFormat>()
function getDurationFormat(
  locale: string,
  style: Exclude<NonNullable<FormatDurationOptions['style']>, 'clock'>,
) {
  const tag = safeLocale(locale)
  const key = `${tag}|${style}`
  const cached = cache.get(key)
  if (cached) return cached

  let df: Intl.DurationFormat
  try {
    df = new Intl.DurationFormat(tag, { style })
  } catch {
    df = new Intl.DurationFormat(FALLBACK_LOCALE, { style })
  }
  cache.set(key, df)
  return df
}

/**
 * Format a count of seconds as a duration. Nullish / `NaN` / non-finite input returns the `'-'`
 * sentinel. `style: 'clock'` renders the deterministic wall clock (`"10:00"`, `"1:02:03"`) with no
 * Intl involvement. Otherwise, when `Intl.DurationFormat` is available the output is locale-aware
 * (default `style` `'narrow'`, mirroring the fallback shape); else the deterministic `Nh Nm Ns`
 * fallback is used. Only the non-zero-relevant units are shown, matching the fallback (e.g. `65s`
 * → `"1m 5s"`, not `"0h 1m 5s"`).
 */
export function formatDuration(
  value: number | null | undefined,
  locale: string,
  options: FormatDurationOptions = {},
): string {
  if (value == null || typeof value !== 'number' || !Number.isFinite(value)) return SENTINEL

  const style = options.style ?? 'narrow'
  if (style === 'clock') return formatClock(value)

  if (typeof Intl.DurationFormat === 'function') {
    try {
      const { hours, minutes, seconds } = splitSeconds(value)
      const parts: Intl.DurationInput = {}
      if (hours > 0) parts.hours = hours
      if (minutes > 0 || hours > 0) parts.minutes = minutes
      parts.seconds = seconds
      return getDurationFormat(locale, style).format(parts)
    } catch {
      // Fall through to the deterministic fallback on any Intl.DurationFormat error.
    }
  }

  return formatDurationFallback(value)
}
